<template>
  <div class="modern-work-orders-view pb-12">
    <!-- Premium Global Header (Master Pattern) -->
    <v-row class="mb-6 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="44" class="mr-4 shadow-sm border-white-sm">
          <v-icon color="white" small>fas fa-industry</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text d-flex align-center lh-1">
            Ordenes de Trabajo
          </div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Gestión y seguimiento de la producción en planta</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center">
        <export-work-orders :disabled="exportDisabled" :filter="filter" :options="options" :search="search"/>
      </div>
    </v-row>

    <!-- Unified Filter Card -->
    <v-card outlined class="modern-card pa-6 mb-8 shadow-sm">
      <v-row dense>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Creada Desde</div>
          <v-text-field 
            v-model='filter.created_after' 
            outlined dense hide-details="auto"
            type="date" 
            :disabled="loading" 
            clearable 
            class="modern-input"
            @change="triggerFetch"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Creada Hasta</div>
          <v-text-field 
            v-model='filter.created_before' 
            outlined dense hide-details="auto"
            type="date" 
            :disabled="loading" 
            clearable 
            class="modern-input"
            @change="triggerFetch"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Entega Desde</div>
          <v-text-field 
            v-model='filter.delivery_after' 
            outlined dense hide-details="auto"
            type="date" 
            :disabled="loading" 
            clearable 
            class="modern-input"
            @change="triggerFetch"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Entrega Hasta</div>
          <v-text-field 
            v-model='filter.delivery_before' 
            outlined dense hide-details="auto"
            type="date" 
            :disabled="loading" 
            clearable 
            class="modern-input"
            @change="triggerFetch"
          />
        </v-col>
      </v-row>
      
      <v-row dense class="mt-4">
        <v-col cols="12" md="4">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Estado de Producción</div>
          <v-select
            v-model="filter.status"
            :items="status"
            item-text="text"
            item-value="value"
            outlined dense hide-details="auto"
            clearable
            :disabled="loading"
            class="modern-select"
            @change="triggerFetch"
          />
        </v-col>
        <v-col cols="12" md="4">
          <Can I="readAll" a="SalesNote">
            <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Vendedor</div>
            <v-select
              v-model="filter.seller"
              :items="sellers"
              item-text="email"
              item-value="id"
              outlined dense hide-details="auto"
              clearable
              :loading="sellersLoading"
              :disabled="loading"
              class="modern-select"
              @change="triggerFetch"
            />
          </Can>
        </v-col>
        <v-col cols="12" md="4">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Tipo de Orden</div>
          <v-select
            v-model="filter.reposicion"
            :items="reposicionOptions"
            item-text="text"
            item-value="value"
            outlined dense hide-details="auto"
            clearable
            placeholder="Todas (Normal y Reposición)"
            :disabled="loading"
            class="modern-select"
            @change="triggerFetch"
          />
        </v-col>
      </v-row>
    </v-card>

    <!-- Main Results Section -->
    <v-card outlined class="modern-card overflow-hidden shadow-sm">
      <v-data-table
        :headers="headers"
        :items="workOrders"
        :options.sync="options"
        :server-items-length="totalWorkOrders"
        :loading="loading"
        show-expand
        single-expand
        :expanded.sync="expanded"
        class="modern-table"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 20, 50, 100],
        }"
        @update:options="fetch"
      >
        <!-- Search Bar in Top Slot (Master Pattern) -->
        <template v-slot:top>
          <div class="px-6 py-4 border-bottom d-flex align-center bg-white">
            <v-text-field
              v-model="search"
              placeholder="Buscar por OT, Cliente o Proyecto..."
              prepend-inner-icon="fas fa-search"
              outlined dense hide-details="auto"
              clearable
              :disabled="loading"
              class="modern-search-field flex-grow-1"
              @keyup="searchTimeOut"
            />
          </div>
        </template>
        <template v-slot:item.id="{ item }">
          <div class="d-flex align-center">
            <show-work-order :work_order_id="item.id" class="OT-link font-weight-black">
              #{{ item.id }}
            </show-work-order>
            <div class="ml-2 d-flex align-center gap-1">
              <v-tooltip bottom v-if="has_prestage(item)">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon x-small v-bind="attrs" v-on="on" color="orange darken-1">fas fa-hourglass-half</v-icon>
                </template>
                <span>En Pre-establecer</span>
              </v-tooltip>
              
              <v-tooltip bottom v-if="item.has_reprocess">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon x-small v-bind="attrs" v-on="on" color="error">fas fa-clock-rotate-left</v-icon>
                </template>
                <span>Reproceso Detectado</span>
              </v-tooltip>

              <v-tooltip bottom v-if="item.sales_note_data && item.sales_note_data.reposicion">
                <template v-slot:activator="{ on, attrs }">
                  <v-chip x-small color="purple" dark label v-bind="attrs" v-on="on" style="font-size:10px;height:18px;padding:0 5px;" class="font-weight-black">
                    R
                  </v-chip>
                </template>
                <span>Cotización de Reposición</span>
              </v-tooltip>

              <v-tooltip bottom v-if="item.has_invoices">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon x-small v-bind="attrs" v-on="on" color="success">fas fa-file-invoice-dollar</v-icon>
                </template>
                <span>Facturas Generadas</span>
              </v-tooltip>

              <v-tooltip bottom v-if="hasRelbaseFactura(item)">
                <template v-slot:activator="{ on, attrs }">
                  <v-chip x-small label v-bind="attrs" v-on="on" style="background-color:#35BDC8;color:white;font-size:10px;height:18px;padding:0 5px;" class="font-weight-black">F</v-chip>
                </template>
                <span>Facturado · Folio {{ getMainFactura(item).folio }} · {{ getMainFactura(item).date_emited | moment('DD/MM/YYYY') }}</span>
              </v-tooltip>
              
              <v-tooltip bottom v-if="softnetEnabled && item.have_softnet_documents">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon x-small v-bind="attrs" v-on="on" class="info--text">fas fa-rss</v-icon>
                </template>
                <span>Enviado a Softnet</span>
              </v-tooltip>
              
              <v-tooltip bottom v-if="kameEnabled && item.have_kame_documents">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon x-small v-bind="attrs" v-on="on" class="orange--text">fas fa-rss</v-icon>
                </template>
                <span>Enviado a Kame</span>
              </v-tooltip>
            </div>
          </div>
        </template>

        <template v-slot:item.created_at="{ item }">
          <span class="caption grey--text font-weight-medium">{{ item.created_at | moment("DD/MM/YYYY") }}</span>
        </template>

        <template v-slot:item.sales_note__name="{ item }">
          <div class="body-2 font-weight-bold grey--text text--darken-3">{{ item.name }}</div>
          <div class="caption primary--text font-weight-bold">CN #{{ item.sales_note }}</div>
        </template>

        <template v-slot:item.sales_note__customer__name="{ item }">
          <span class="body-2 font-weight-medium">{{ item.customer_name }}</span>
        </template>

        <template v-slot:item.delivery_date="{ item }">
          <div class="caption font-weight-bold" :class="isLate(item) ? 'error--text' : 'grey--text text--darken-2'">
            {{ item.delivery_date | moment("DD/MM/YYYY") }}
          </div>
          <div class="caption grey--text">{{ item.delivery_date | moment("HH:mm") }} hrs</div>
        </template>

        <template v-slot:item.progress="{ item }">
          <div class="d-flex align-center justify-center">
            <v-progress-linear
              :value="item.progress"
              :color="getProgressColor(item.progress)"
              height="8"
              rounded
              class="flex-grow-1"
              style="min-width: 80px"
              striped
            />
            <span class="caption font-weight-black ml-2" :class="getProgressTextColor(item.progress)">
              {{ Math.ceil(item.progress) }}%
            </span>
          </div>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-menu offset-y left transition="slide-y-transition" rounded="lg">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on">
                <v-icon small>fas fa-ellipsis-v</v-icon>
              </v-btn>
            </template>
            <v-list dense class="py-1">
              <v-list-item class="px-3 py-1">
                <print-work-order :workorder_id="item.id" :loadWorkOrder="true" />
              </v-list-item>
              
              <v-list-item v-if="!item.completed && $can('update', 'WorkOrder')" @click="update(item)" class="px-3 py-1">
                <v-list-item-icon class="mr-3 ml-0"><v-icon small color="primary">fas fa-edit</v-icon></v-list-item-icon>
                <v-list-item-title class="body-2 font-weight-medium">Modificar</v-list-item-title>
              </v-list-item>

              <v-list-item v-if="$can('readCost', 'SalesNote')" @click="generateCostReport(item)" class="px-3 py-1">
                <v-list-item-icon class="mr-3 ml-0"><v-icon small color="blue-grey">fas fa-calculator</v-icon></v-list-item-icon>
                <v-list-item-title class="body-2 font-weight-medium">Informe de Costos</v-list-item-title>
              </v-list-item>

              <v-list-item v-if="relbaseEnabled" @click="openIssuanceDialog(item)" class="px-3 py-1">
                <v-list-item-icon class="mr-3 ml-0"><v-icon small color="accent">fas fa-file-invoice-dollar</v-icon></v-list-item-icon>
                <v-list-item-title class="body-2 font-weight-medium accent--text">Emitir Relbase</v-list-item-title>
              </v-list-item>

              <v-list-item v-if="softnetEnabled && $can('create', 'Softnet')" class="px-3 py-1">
                <softnet-button :sales_note="item.sales_note" />
              </v-list-item>

              <v-list-item v-if="kameEnabled && $can('create', 'Kame')" class="px-3 py-1">
                <kame-button :sales_note="item.sales_note" />
              </v-list-item>

              <v-divider v-if="item.completed && $can('delete', 'WorkOrder')" class="my-1"></v-divider>

              <v-list-item v-if="!item.completed && $can('delete', 'WorkOrder')" @click="confirmDelete(item)" class="px-3 py-1">
                <v-list-item-icon class="mr-3 ml-0"><v-icon small color="error">fas fa-trash-alt</v-icon></v-list-item-icon>
                <v-list-item-title class="body-2 font-weight-medium error--text">Eliminar</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

        <!-- Expanded Item -->
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="pa-0 bg-grey-light">
            <div class="pa-6 border-left-accent">
              <div class="d-flex align-center mb-4">
                <v-icon x-small color="primary" class="mr-2">fas fa-stream</v-icon>
                <span class="text-subtitle-2 font-weight-bold grey--text text--darken-3">Detalle de Productos en Producción</span>
              </div>
              
              <v-card outlined class="modern-card elevation-0">
                <v-simple-table dense class="modern-table transparent">
                  <thead>
                    <tr>
                      <th class="text-center" width="50">Pos.</th>
                      <th class="text-left">Nombre de Producto</th>
                      <th class="text-right" width="100">Cantidad</th>
                      <th class="text-right" width="120">Superficie</th>
                      <th class="text-center" width="200">Estado / Avance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(product, idx) in item.products" :key="idx">
                      <td class="text-center caption grey--text font-weight-bold py-2">{{ idx + 1 }}</td>
                      <td>
                        <div class="body-2 font-weight-medium primary--text">{{ product.name }}</div>
                      </td>
                      <td class="text-right body-2">{{ product.qty }}</td>
                      <td class="text-right font-weight-bold caption">{{ product.mts2 | number('0.00') }} m²</td>
                      <td>
                        <div class="d-flex align-center justify-center">
                          <v-chip v-if="product.in_prestage" :color="product.prestage_color" x-small label outlined class="font-weight-bold text-uppercase">
                            {{ product.prestage_name }}
                          </v-chip>
                          <div v-else class="flex-grow-1 px-4">
                            <v-progress-linear :value="product.progress" color="accent" height="6" rounded />
                            <div class="caption text-center primary--text font-weight-bold mt-1">{{ Math.floor(product.progress) }}%</div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-card>
            </div>
          </td>
        </template>
      </v-data-table>
    </v-card>

    <!-- Modals -->
    <v-dialog v-model='display' max-width="1200" persistent>
      <v-card outlined class="modern-card overflow-hidden">
        <work-order-card 
          v-if="display && workOrderOnDisplay && workOrderOnDisplay.id"
          :work_order.sync='workOrderOnDisplay'
          :key="workOrderVersion"
          @close="display=false" 
          @refresh="fetch"
          :loading="workOrderOnDisplayLoading"
        /> 
      </v-card>
    </v-dialog>

    <confirm
      v-if="workOrdertoDelete"
      v-model="showDeleteOrder"
      :message="`¿Estás seguro que deseas eliminar la Orden de Trabajo # ${workOrdertoDelete.id}?`"
      @ok="deleteWorkOrder(workOrdertoDelete)"
      @cancel="showDeleteOrder=false"
    />

    <!-- Relbase Issuance Dialog -->
    <relbase-issuance-dialog
      v-if="relbaseEnabled"
      v-model="showIssuanceDialog"
      :ot-id="selectedOtId"
      @issued="fetch"
    />
  </div>
</template>

<script>
import {fetchWorkOrders,getWorkOrder,deleteWorkOrder} from '@/api/production'
import {getSalesNote} from '@/api/salesNotes'
import WorkOrderCard from '@/components/production/WorkOrderCard'
import ExportWorkOrders from '@/components/production/ExportWorkOrders'
import RelbaseIssuanceDialog from '@/components/relbase/RelbaseIssuanceDialog.vue'
import SoftnetButton from '@/components/softnet/SoftnetButton.vue'
import KameButton from '@/components/kame/KameButton.vue'
import notifications from '@/utils/notifications'
import moment from 'moment'
import PrintWorkOrder from '@/components/production/PrintWorkOrder'

export default {
  name: 'WorkOrders',
  components: { WorkOrderCard, ExportWorkOrders, PrintWorkOrder, RelbaseIssuanceDialog, SoftnetButton, KameButton },
  data() {
    return {
      loading: false,
      showIssuanceDialog: false,
      selectedOtId: null,
      status: [
        { text: 'Vigentes', value: false },
        { text: 'Completadas', value: true },
        { text: 'Todas las Órdenes', value: '' }
      ],
      reposicionOptions: [
        { text: 'Reposición', value: 'true' },
        { text: 'Normal', value: 'false' },
      ],
      filter: {
        created_after: '',
        created_before: '',
        delivery_after: '',
        delivery_before: '',
        status: false,
        seller: '',
        reposicion: ''
      },
      workOrderOnDisplay: {},
      workOrderOnDisplayLoading: false,
      options: {},
      totalWorkOrders: 0,
      display: false,
      search: '',
      workOrders: [],
      sellers: [],
      sellersLoading: false,
      workOrdertoDelete: null,
      expanded: [],
      timer: null,
      workOrderVersion: 0,
      headers: [
        { text: 'N° OT', align: 'start', value: 'id', class: 'grey lighten-4', width: '100' },
        { text: 'Creada', align: 'start', value: 'created_at', class: 'grey lighten-4', width: '120' },
        { text: 'Proyecto / Nota', align: 'start', value: 'sales_note__name', class: 'grey lighten-4' },
        { text: 'Cliente', align: 'start', value: 'sales_note__customer__name', class: 'grey lighten-4' },
        { text: 'Vencimiento', align: 'start', value: 'delivery_date', class: 'grey lighten-4', width: '140' },
        { text: 'Avance General', value: 'progress', align: 'center', class: 'grey lighten-4', width: '180', sortable: false },
        { text: 'Acciones', value: 'actions', align: 'center', class: 'grey lighten-4', sortable: false, width: '80' },
        { text: '', value: 'data-table-expand', class: 'grey lighten-4', sortable: false, width: '50' }
      ],
      loading_print_cost_report: false
    }
  },
  computed: {
    relbaseEnabled() {
      return !!this.$store.getters.getCompany?.relbase_integration_enabled
    },
    softnetEnabled() {
      return !!this.$store.getters.getCompany?.softnet_integration_enabled
    },
    kameEnabled() {
      return !!this.$store.getters.getCompany?.kame_integration_enabled
    },
    exportDisabled() {
      const hasCreatedAfter = this.filter.created_after && this.filter.created_after !== ''
      const hasDeliveryAfter = this.filter.delivery_after && this.filter.delivery_after !== ''
      return !hasCreatedAfter && !hasDeliveryAfter
    }
  },
  watch: {
    workOrderOnDisplay: {
      handler() {
        this.workOrderVersion++
      },
      deep: false
    }
  },
  methods: {
    hasRelbaseFactura(item) {
      return item.relbase_documents && item.relbase_documents.some(d => d.doc_type === 33)
    },
    getMainFactura(item) {
      return item.relbase_documents ? item.relbase_documents.find(d => d.doc_type === 33) || {} : {}
    },
    openIssuanceDialog(item) {
      this.selectedOtId = item.id
      this.showIssuanceDialog = true
    },
    triggerFetch() {
      this.options.page = 1
      this.fetch()
    },
    isLate(item) {
      return moment().isAfter(moment(item.delivery_date)) && item.progress < 100
    },
    getProgressColor(progress) {
      if (progress >= 100) return 'success'
      if (progress > 60) return 'accent'
      if (progress > 30) return 'primary'
      return 'grey lighten-1'
    },
    getProgressTextColor(progress) {
      if (progress >= 100) return 'success--text'
      if (progress > 0) return 'primary--text'
      return 'grey--text'
    },
    has_prestage(workOrder) {
      return workOrder.products && workOrder.products.some(p => p.in_prestage)
    },
    confirmDelete(item) {
      this.workOrdertoDelete = item
      this.showDeleteOrder = true
    },
    async generateCostReport(item) {
      this.loading_print_cost_report = true
      try {
        const data = await getSalesNote(item.sales_note)
        const {pdfCostReport} = await import('@/doc_generators/costReport')
        pdfCostReport(data)
      } catch (err) {
        notifications.showError('Error generando informe: ' + err)
      } finally {
        this.loading_print_cost_report = false
      }
    },
    async fetch() {
      this.loading = true
      const { sortBy, sortDesc, page, itemsPerPage } = this.options
      
      let created_before = ''
      let created_after = ''
      if (this.filter.created_after) created_after = moment(this.filter.created_after).utc().format('YYYY-MM-DD HH:mm')
      if (this.filter.created_before) created_before = moment(this.filter.created_before).utc().add(1439, 'minutes').format('YYYY-MM-DD HH:mm')
      
      const sort = sortBy && sortBy[0] ? (sortDesc[0] ? `${sortBy[0]}` : `-${sortBy[0]}`) : '-created_at'
      
      try {
        const data = await fetchWorkOrders(
          page || 1,
          itemsPerPage || 20,
          this.search,
          created_after,
          created_before,
          this.filter.delivery_after || '',
          this.filter.delivery_before || '',
          this.filter.status === null ? '' : this.filter.status,
          this.filter.seller || '',
          sort,
          this.filter.reposicion || ''
        )
        
        this.totalWorkOrders = data.count
        this.workOrders = data.results.map(w => {
          let completedMts2 = 0
          w.products.forEach(p => {
            const completed = p.process_orders ? p.process_orders.filter(c => c.completed).length : 0
            const total = p.process_orders ? p.process_orders.length : 1
            p.progress = (completed / total) * 100
            completedMts2 += (p.progress / 100) * (p.mts2 || 0)
          })
          w.progress = w.total_mts2 > 0 ? (completedMts2 / w.total_mts2) * 100 : 0
          return w
        })
      } catch (err) {
        notifications.showError('Error cargando OT: ' + err)
      } finally {
        this.loading = false
      }
    },
    searchTimeOut() {
      if (this.timer) clearTimeout(this.timer)
      this.timer = setTimeout(() => { this.fetch() }, 800)
    },
    update(item) {
      this.$router.push({ name: 'WorkOrderUpdate', params: { id: item.id } })
    },
    async deleteWorkOrder(item) {
      try {
        await deleteWorkOrder(item.id)
        notifications.showSuccess(`Orden de Trabajo ${item.id} eliminada`)
        this.showDeleteOrder = false
        this.fetch()
      } catch (e) {
        notifications.showError(`Error al eliminar: ${e}`)
      }
    }
  },
  async mounted() {
    this.fetch()
    this.sellers = this.$store.getters.getSellers
  }
}
</script>

<style scoped>
.modern-card {
  border: 1px solid rgba(0,0,0,0.08) !important;
  border-radius: 12px !important;
}
.bg-grey-light { background-color: #f8f9fa; }
.border-left-accent { border-left: 4px solid var(--v-accent-base) !important; }
.OT-link { cursor: pointer; color: var(--v-primary-base); text-decoration: none; }
.OT-link:hover { text-decoration: underline; }
.gap-1 { gap: 4px; }
.modern-table >>> .v-data-table__expanded__content { background: #f8f9fa !important; box-shadow: inset 0 2px 4px rgba(0,0,0,0.05) !important; }
.modern-table >>> .v-data-table__expanded__content { background: #f8f9fa !important; box-shadow: inset 0 2px 4px rgba(0,0,0,0.05) !important; }
</style>
